import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Download } from "lucide-react";
import { useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { Badge, Spinner } from "@/components/ui/card";
import { Dialog } from "@/components/ui/dialog";
import { useToast } from "@/components/ui/toast";
import { api, errorMessage, unwrap } from "@/lib/api";
import { downloadBlob } from "@/lib/exportPng";
import { useT } from "@/lib/i18n";

/** Excel'dan ommaviy import: shablonni yuklab olish, fayl yuborish, natija va qator xatolarini ko'rish. */
export function ImportDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const { t } = useT();
  const toast = useToast();
  const qc = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [jobId, setJobId] = useState<string | null>(null);

  const template = useMutation({
    mutationFn: async () => {
      const blob = unwrap(
        await api.GET("/api/products/import-template/", { parseAs: "blob" }),
      ) as unknown as Blob;
      downloadBlob(blob, "mahsulotlar-shablon.xlsx");
    },
    onError: (e) => toast.error(errorMessage(e, t("common.error"))),
  });

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.append("file", file);
      const res = await api.POST("/api/product-imports/", {
        body: form as never,
        bodySerializer: (b: unknown) => b as FormData,
      });
      return unwrap(res);
    },
    onSuccess: (job) => setJobId(job.id),
    onError: (e) => toast.error(errorMessage(e, t("common.error"))),
  });

  const job = useQuery({
    queryKey: ["product-import", jobId],
    enabled: !!jobId,
    queryFn: async () => {
      const j = unwrap(
        await api.GET("/api/product-imports/{id}/", { params: { path: { id: jobId! } } }),
      );
      if (j.status === "done") {
        void qc.invalidateQueries({ queryKey: ["catalog"] });
        void qc.invalidateQueries({ queryKey: ["categories"] });
        void qc.invalidateQueries({ queryKey: ["brands"] });
      }
      return j;
    },
    refetchInterval: (q) =>
      q.state.data && ["done", "failed"].includes(q.state.data.status) ? false : 1500,
  });

  const j = job.data;
  const running =
    upload.isPending || (!!jobId && (!j || ["pending", "processing"].includes(j.status)));

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) setJobId(null);
        onOpenChange(o);
      }}
      title={t("import.title")}
      description={t("import.description")}
    >
      <div className="flex flex-col gap-4">
        <Button variant="outline" disabled={template.isPending} onClick={() => template.mutate()}>
          <Download className="size-4" /> {t("import.download_template")}
        </Button>
        <input
          ref={fileInput}
          type="file"
          hidden
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          data-testid="import-file"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) {
              setJobId(null);
              upload.mutate(f);
            }
            e.target.value = "";
          }}
        />
        <Button disabled={running} onClick={() => fileInput.current?.click()}>
          {running ? t("import.processing") : t("import.choose_file")}
        </Button>
        {running && <Spinner />}

        {j?.status === "failed" && (
          <p role="alert" className="rounded-lg bg-danger/10 p-3 text-sm text-danger">
            {j.error}
          </p>
        )}
        {j?.status === "done" && (
          <div className="flex flex-col gap-2" role="status">
            <div className="flex flex-wrap gap-2">
              <Badge tone="success">{t("import.created", { n: j.created_count })}</Badge>
              <Badge tone="info">{t("import.updated", { n: j.updated_count })}</Badge>
              {j.errors.length > 0 && (
                <Badge tone="danger">{t("import.errors", { n: j.errors.length })}</Badge>
              )}
            </div>
            {j.errors.length > 0 && (
              <ul
                className="max-h-48 overflow-y-auto rounded-lg border border-border text-sm"
                aria-label={t("import.error_list")}
              >
                {j.errors.map((e, i) => (
                  <li key={i} className="border-b border-border p-2 last:border-0">
                    <strong>{t("import.row", { n: (e as { row: number }).row })}:</strong>{" "}
                    {(e as { message: string }).message}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </Dialog>
  );
}
